import { type FC, useEffect, useCallback, useState } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy';
import SV from 'server-vars';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId } from 'dibs-cookie-jar';

import { OverlaySpinner } from 'dibs-elements/exports/OverlaySpinner';
import * as tracking from 'dibs-tracking';
import { trackRelatedSearchClick } from 'dibs-interiors-photos/exports/trackingHelpers';

import { PHOTOS_BROWSE_PAGE_LIMIT } from '../constants';
import { Pagination } from '../shared/Pagination';
import { PhotosSearchBrowseQuery } from './PhotosSearchBrowseQuery';
import { PhotosSearchBrowseContent } from './PhotosSearchBrowseContent';
import { PhotosSearchBrowseBeardcrumbs } from './PhotosSearchBrowseBeardcrumbs';
import { PhotosSearchBrowseFacetFilterModal } from './PhotosSearchBrowseFacetFilterModal';
import { HeadlineContextProvider } from './PhotosSearchBrowseHeadlineContext';
import { PhotosSearchBrowseFacetFilter } from './PhotosSearchBrowseFacetFilter';
import PhotosSearchBrowsePhotoPreviewModal from './PhotoModal/PhotosSearchBrowsePhotoPreviewModal';
import { PhotosSearchBrowseWrapperHeader } from './PhotosSearchBrowseWrapperHeader';
import {
    CHANGE_FACET,
    ADD_RELATED_DESIGN_FACET,
    DELETE_FACET,
    RESET_SEARCH,
    SEARCH_PHOTOS,
} from './helpers/useFacetReducer';
import { NAVIGATE } from './helpers/useHistoryReducer';
import { usePhotosSearchBrowseState } from './helpers/usePhotosSearchBrowseState';
import { useServerVars } from '../shared/ServerVarsContext';
import PhotosSearchBrowseRelatedDesignsContainer from './PhotosSearchBrowseRelatedDesignsContainer';

import { type PhotosSearchBrowseWrapper_viewer$data } from './__generated__/PhotosSearchBrowseWrapper_viewer.graphql';
import { type ModifyFacetPayload, type RelayVariables } from './types';

import styles from './PhotosSearchBrowseWrapper.scss';

const getUserId = (): string => getBuyerId(document.cookie);

type PhotosSearchBrowseWrapperComponentProps = {
    relay: RelayRefetchProp;
    relayVariables: RelayVariables;
    viewer: PhotosSearchBrowseWrapper_viewer$data | null | undefined;
    originalUrl: string;
};

export const PhotosSearchBrowseWrapperComponent: FC<PhotosSearchBrowseWrapperComponentProps> = ({
    viewer,
    relay,
    relayVariables,
    originalUrl: originalUrlProp,
}) => {
    const photosQuery = viewer?.photosSearchBrowseQuery || null;
    const totalResults = photosQuery?.totalResults || 0;
    // FI-2187 allow to paginate max to 50
    // with bots crawling high number paginated pages we encounter performance issues
    let numberOfPages = photosQuery?.numberOfPages || 0;
    if (numberOfPages > PHOTOS_BROWSE_PAGE_LIMIT) {
        numberOfPages = PHOTOS_BROWSE_PAGE_LIMIT;
    }
    const allFacets = photosQuery?.facets;

    const {
        handleFacetChange,
        getPaginationHref,
        handlePaginate,
        originalUrl,
        pathFacets,
        facetFilter,
        facetFilterPath,
        modalPhoto,
        isLoading,
        dispatchHistory,
        dispatchFacetFilter,
        dispatchModalPhoto,
    } = usePhotosSearchBrowseState({
        originalUrl: originalUrlProp,
        relay,
        relayVariables,
        facets: allFacets,
        photosEdges: photosQuery?.edges || [],
        numberOfPages,
        totalResults,
    });
    const { page, searchTerm, style, room, project, creator } = facetFilter;

    const [userId, setUserId] = useState('');
    useEffect(() => {
        setUserId(getUserId());
        addLoginHandlers(() => {
            setUserId(getUserId());
        });
    }, []);

    useEffect(() => {
        tracking.trackPageview({
            url: window.location.href,
            additional: SV.get('tracking.initialDataLayerInfo'),
        });
    }, [page, searchTerm, style, room, project, creator]);

    const { isDesktop } = useServerVars();

    const facetSearch = {
        searchTerm,
        style,
        room,
        project,
        creator,
        page,
    };

    const onPhotoClick = useCallback(
        (path: string) => {
            dispatchHistory({ type: NAVIGATE, path });
        },
        [dispatchHistory]
    );

    const onRelatedDesignClick = useCallback(
        (payload: Partial<ModifyFacetPayload>) => {
            handleFacetChange({ action: ADD_RELATED_DESIGN_FACET, payload });
            trackRelatedSearchClick({ label: 'photos' });
        },
        [handleFacetChange]
    );

    return (
        <HeadlineContextProvider facetFilter={facetFilter}>
            <PhotosSearchBrowseBeardcrumbs pathFacet={pathFacets[0]} />
            <PhotosSearchBrowseWrapperHeader />
            <div className={styles.container}>
                {isDesktop && (
                    <div className={styles.facetFilter}>
                        <PhotosSearchBrowseFacetFilter
                            viewer={viewer}
                            photos={photosQuery}
                            originalUrl={originalUrl}
                            facetSearch={facetSearch}
                            onFacetChange={payload =>
                                handleFacetChange({ action: CHANGE_FACET, payload })
                            }
                            onFacetDelete={payload =>
                                handleFacetChange({ action: DELETE_FACET, payload })
                            }
                            onFacetsClear={() => handleFacetChange({ action: RESET_SEARCH })}
                            onSearch={payload =>
                                handleFacetChange({ action: SEARCH_PHOTOS, payload })
                            }
                        />
                    </div>
                )}

                <div className={styles.photosContent}>
                    <PhotosSearchBrowseRelatedDesignsContainer
                        photosRef={photosQuery}
                        onRelatedDesignClick={onRelatedDesignClick}
                        facetSearch={facetSearch}
                    />
                    {!isDesktop && (
                        <PhotosSearchBrowseFacetFilterModal
                            photos={photosQuery}
                            viewer={viewer}
                            facetSearch={facetSearch}
                            onFacetChange={payload =>
                                handleFacetChange({ action: CHANGE_FACET, payload })
                            }
                            onFacetDelete={payload =>
                                handleFacetChange({ action: DELETE_FACET, payload })
                            }
                            onFacetsClear={() => handleFacetChange({ action: RESET_SEARCH })}
                            onSearch={payload =>
                                handleFacetChange({ action: SEARCH_PHOTOS, payload })
                            }
                        />
                    )}
                    <PhotosSearchBrowseContent
                        originalUrl={originalUrlProp}
                        userId={userId}
                        photos={photosQuery}
                        onPhotoClick={onPhotoClick}
                    />
                    <PhotosSearchBrowseRelatedDesignsContainer
                        photosRef={photosQuery}
                        onRelatedDesignClick={onRelatedDesignClick}
                        facetSearch={facetSearch}
                    />
                    <Pagination
                        page={page}
                        totalResults={totalResults}
                        numberOfPages={numberOfPages}
                        pageTilesToDisplay={isDesktop ? 8 : 6}
                        href={getPaginationHref}
                        handlePaginate={handlePaginate}
                    />
                </div>
            </div>
            <PhotosSearchBrowsePhotoPreviewModal
                isOpen={modalPhoto.isOpen}
                facetFilter={facetFilter}
                photo={modalPhoto.photo}
                modalPhotoIndex={modalPhoto.photoIndex}
                totalResults={totalResults}
                photos={photosQuery}
                onClose={() => dispatchHistory({ type: NAVIGATE, path: facetFilterPath })}
                dispatchFacetFilter={dispatchFacetFilter}
                dispatchModalPhoto={dispatchModalPhoto}
                dispatchHistory={dispatchHistory}
                userId={userId}
            />
            <OverlaySpinner
                dataTn="photo-overlay-spinner"
                isOpen={isLoading}
                trapFocus={false}
                preventBackScroll={false}
            />
        </HeadlineContextProvider>
    );
};

export const PhotosSearchBrowseWrapper = createRefetchContainer(
    PhotosSearchBrowseWrapperComponent,
    {
        viewer: graphql`
            fragment PhotosSearchBrowseWrapper_viewer on Viewer {
                ...PhotosSearchBrowseFacetFilterModal_viewer
                ...PhotosSearchBrowseFacetFilter_viewer
                photosSearchBrowseQuery(
                    page: $page
                    first: $first
                    searchTerm: $searchTerm
                    style: $style
                    room: $room
                    project: $project
                    creator: $creator
                ) {
                    ...PhotosSearchBrowseFacetFilterModal_photos
                    ...PhotosSearchBrowseFacetFilter_photos
                    ...PhotosSearchBrowseContent_photos
                    ...PhotosSearchBrowsePhotoPreviewModal_photos
                    ...PhotosSearchBrowseRelatedDesignsContainer_photos
                    facets {
                        imageStyleFacet {
                            label
                            name
                        }
                        imageRoomTypeFacet {
                            label
                            name
                        }
                        designProjectTypeFacet {
                            label
                            name
                        }
                        imageCreatorFacets {
                            label
                            name
                        }
                    }
                    edges {
                        node {
                            ...PhotosSearchBrowsePhotoPreviewModal_photo
                            url
                            name
                            room {
                                name
                            }
                            designFirm {
                                name
                            }
                        }
                    }
                    totalResults
                    numberOfPages
                }
            }
        `,
    },
    PhotosSearchBrowseQuery
);
